import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from './Seo';
import Breadcrumbs, { buildBreadcrumbSchema } from './Breadcrumbs';
import './App.css';
import './PrivacyPolicy.css';
import Footer from './Footer';
import Navbar from './Navbar';

const breadcrumbItems = [
  { name: 'Home', path: '/' },
  { name: 'Refund Policy', path: '/refund-policy' },
];

const sections = [
  {
    id: 'overview',
    badge: '01',
    title: 'Overview',
    body: `Thank you for choosing Baatcheet, our audio call and communication service.

If you are not fully satisfied with your experience, we encourage you to go through our refund and return policy below. This policy details the terms and conditions that apply to any transactions made through our platform.`,
  },
  {
    id: 'definitions',
    badge: '02',
    title: 'Interpretation and Definitions',
    body: `**Interpretation**

Terms beginning with a capital letter carry specific meanings as described below. These definitions hold true whether the terms are used in singular or plural form.

**Definitions**

For the purposes of this Return and Refund Policy: The following terms apply to your use of the Baatcheet website (https://baatcheetapp.in/ — hereafter the "Website") and the Baatcheet mobile application (Android application — hereafter the "App"), provided by Deveffica Private Limited (hereafter "we", "us", "our", or "Baatcheet"). Please read these carefully. If you have any objection to any part of these terms or our privacy policy, kindly refrain from using the Website, App, or any other service offered by us.`,
  },
  {
    id: 'cancellation-rights',
    badge: '03',
    title: 'Your Order Cancellation Rights',
    body: `As Baatcheet deals exclusively in digital goods and not physical products, no cancellation or return facility is available. Users may make payments to acquire digital items that can be used to engage with other Baatcheet users. Digital goods include messages, coins, diamonds, and similar in-app items. New digital goods may be introduced, and existing ones may be updated or removed at any time. Digital goods received through referral programs will expire within 12 months of being credited.`,
  },
  {
    id: 'iap',
    badge: '04',
    title: 'In-App Purchases',
    body: `When completing an in-app purchase, you will be asked to provide details linked to your account on the relevant Software Store ("your IAP Account"). Your IAP Account will be billed for the purchase in accordance with the terms presented to you at the time of the transaction, as well as the general in-app purchase terms applicable to your IAP Account. Depending on your location, some Software Stores may apply applicable sales taxes to your purchase.`,
  },
  {
    id: 'subscriptions',
    badge: '05',
    title: 'Subscription Services',
    body: `If you opt for an auto-renewing periodic subscription via an in-app purchase, your IAP Account will be billed on a recurring basis until the subscription is cancelled by you. Once the initial subscription period concludes — and at the end of every subsequent period — your subscription will automatically renew for an equivalent duration at the rate you agreed to at the time of subscribing.

If you do not want your subscription to renew, or if you intend to modify or cancel it, you must log into your IAP Account and follow the relevant cancellation steps — even if you have already deleted your Baatcheet account or removed the Baatcheet app from your device.

Please note that deleting your Baatcheet account or uninstalling the app does not constitute a cancellation of your subscription. Baatcheet will continue to retain all charges billed to your IAP Account until you formally cancel through your IAP Account.`,
  },
  {
    id: 'refund-policy',
    badge: '06',
    title: 'Refund Policy',
    body: `As a general rule, all payments made on Baatcheet are final and non-refundable under any circumstances. This means you are not entitled to cancel a payment or seek a refund for any reason, including in the event of account termination.

An exception may be considered if a refund request is submitted within 14 days of the transaction date, provided that the payment was made in a currency other than INR, the purchased goods remain entirely unused, and the laws applicable in your jurisdiction mandate refund eligibility. Where applicable, such refunds will be processed within 7 business days.`,
  },
  {
    id: 'platform-refunds',
    badge: '07',
    title: 'Platform-Specific Refunds',
    body: `**Apple iOS:** For any payment-related concerns regarding transactions made through iOS devices using your Apple ID, please reach out directly to Apple. Baatcheet is unable to process such refunds. To raise a refund request, visit https://getsupport.apple.com.

**Google Play Store:** If your payment was processed via the Google Play Store, please get in touch with Baatcheet support by writing to baatcheet.connect@gmail.com, including your order details and the invoice received from the Google Play Store.

**Website / Other Payment Gateways:** For payments made through our Website or any other payment gateway, please contact our support team at baatcheet.connect@gmail.com with your order details and the corresponding invoice.`,
  },
  {
    id: 'contact',
    badge: '08',
    title: 'Contact Us',
    body: `For any queries regarding this Return and Refund Policy, feel free to reach out to us:

**By email:** baatcheet.connect@gmail.com

**By address:** 11/547, Patel Nagar, Gali No. 2, Near BSNL Office, Baraut, Baghpat, Uttar Pradesh, India, 250611`,
  },
];

function RefundPolicy() {
  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.08 }
    );
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <Seo
        title="Refund & Cancellation Policy | Baatcheet"
        description="Baatcheet's Refund and Cancellation Policy for in-app purchases, subscriptions, and Premium Services."
        path="/refund-policy"
        jsonLd={buildBreadcrumbSchema(breadcrumbItems)}
      />
      <div className="bg" />
      <div className="confetti">
        <i className="dot s1" /><i className="cbar s2" /><i className="dot s3" /><i className="cbar s4" />
        <i className="dot s5" /><i className="dot s6" /><i className="cbar s7" /><i className="dot s8" />
        <i className="cbar s9" /><i className="dot s10" />
      </div>

      <Navbar />
      <Breadcrumbs items={breadcrumbItems} />

      <main>

        {/* ── Hero ── */}
        <section className="pp-hero shell reveal">
          <div className="pp-hero-tag">Legal Documents</div>
          <h1 className="pp-hero-title">
            Returns &amp;<br />
            <span className="pp-hero-accent">Refund Policy</span>
          </h1>
          <p className="pp-hero-sub">
            All purchases on Baatcheet are for digital goods. This Policy explains how payments work, when refunds may apply, and how to raise a query with our support team.
          </p>
          <div className="pp-meta">
            <span className="pp-meta-item">Last Updated: May 2026</span>
            <span className="pp-meta-sep">·</span>
            <span className="pp-meta-item">Governed by Indian Law</span>
            <span className="pp-meta-sep">·</span>
            <a className="pp-meta-item pp-meta-link" href="mailto:baatcheet.connect@gmail.com?subject=Refund%20Query">Raise a Query</a>
          </div>
        </section>

        {/* ── Table of Contents ── */}
        <section className="pp-toc-wrap shell reveal">
          <div className="pp-toc">
            <div className="pp-toc-label">Jump to section</div>
            <div className="pp-toc-grid">
              {sections.map(s => (
                <a key={s.id} className="pp-toc-item" href={`#${s.id}`}>
                  <span className="pp-toc-num">{s.badge}</span>
                  <span className="pp-toc-name">{s.title}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ── Policy Sections ── */}
        <section className="pp-body shell">
          {sections.map((s, i) => (
            <article key={s.id} id={s.id} className={`pp-article reveal ${i % 2 === 1 ? 'pp-article--alt' : ''}`}>
              <div className="pp-article-num">{s.badge}</div>
              <div className="pp-article-content">
                <h2 className="pp-article-title">{s.title}</h2>
                <div className="pp-article-body">
                  {s.body.split('\n\n').map((para, pi) => {
                    if (para.startsWith('**') && para.endsWith('**') && para.indexOf('**', 2) === para.length - 2) {
                      return <h3 key={pi} className="pp-subhead">{para.slice(2, -2)}</h3>;
                    }
                    if (para.startsWith('— ')) {
                      const items = para.split('\n').filter(Boolean);
                      return (
                        <ul key={pi} className="pp-bullets">
                          {items.map((item, ii) => (
                            <li key={ii}>{item.replace(/^— /, '')}</li>
                          ))}
                        </ul>
                      );
                    }
                    return (
                      <p key={pi} dangerouslySetInnerHTML={{
                        __html: para.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                      }} />
                    );
                  })}
                </div>
              </div>
            </article>
          ))}
        </section>

        {/* ── CTA strip ── */}
        <section className="pp-cta shell reveal">
          <div className="pp-cta-inner">
            <div className="pp-cta-text">
              <h3>Need help with a payment?</h3>
              <p>Our support team resolves all payment queries within 7 business days.</p>
            </div>
            <a
              className="btn btn-cyan"
              href="mailto:baatcheet.connect@gmail.com?subject=Refund%20Query"
            >
              ✉ Contact Support
            </a>
          </div>
        </section>

        <div className="pp-related shell">
          <span>Related:</span>
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/terms-of-service">Terms of Service</Link>
          <Link to="/account-deletion">Account Deletion</Link>
          <Link to="/download">Download the App</Link>
        </div>

      </main>

      <Footer />
    </>
  );
}

export default RefundPolicy;
